import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { MoreMenu } from '@/card/record/RecordHeader'
import { ConfirmModal } from '@/card/record/ConfirmModal'
import { StateView } from '@/card/record/StateView'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import type { Locale } from '@/i18n'
import { createSchedule, deleteSchedule, fetchReports, fetchScheduleDeliveries, fetchSchedules, updateSchedule } from './api'
import { isForbidden } from './reportKit'
import type { ReportDelivery, ReportSchedule, SavedReport, SaveScheduleRequest } from './types'
import '@/styles/work-analytics.css'

/**
 * Рассылки отчётов (ReportMailings.dc.html; ТЗ §4.6, тикет #76).
 *
 * ## Что здесь важно не спрятать
 *
 * Расписание — обещание, что письмо придёт. Поэтому видны обе вещи, по которым видно, что
 * обещание выполняется: когда ушло в прошлый раз и когда уйдёт в следующий. Расписание без
 * `nextRunAt` — сломанное, и это сказано словом, а не прочерком среди дат.
 *
 * ## Периодичность — выбором, cron — под капотом
 *
 * Форма собирает пятипольный cron из «ежедневно / по дням недели / числа месяца» и времени.
 * Расписание, которое так не описывается (заведено раньше руками), правится как есть —
 * полем cron, а не подменяется ближайшим похожим. Проверка выражения — на сервере при
 * сохранении: неразбираемое отвергается сразу, а не молчит навсегда.
 *
 * ## Чего нет из макета
 *
 * Результата последней отправки в строке таблицы (исход писем — в журнале доставки на форме
 * расписания, #139), состояния
 * «почта не настроена», часового пояса (сервер считает по своему), темы письма, тестовой
 * отправки, выбора получателей из справочника (адрес обязан принадлежать учётной записи — сервер
 * отвергает чужой словами) и PDF — сервер его не формирует и
 * расписание с ним не сохраняет. Формат выбирается из CSV и XLSX (#88).
 */

type Mode = 'daily' | 'weekly' | 'monthly' | 'cron'

interface Draft {
  id: string | null
  reportId: string
  mode: Mode
  days: number[]
  dayOfMonth: number
  time: string
  cron: string
  emails: string[]
  isActive: boolean
  format: 'csv' | 'xlsx'
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0]

export function SchedulesPage() {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [deleting, setDeleting] = useState<ReportSchedule | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'off' | 'broken' | null>(null)

  const schedules = useQuery({
    queryKey: ['report-schedules'],
    queryFn: ({ signal }) => fetchSchedules(signal),
    retry: false,
  })
  const reports = useQuery({
    queryKey: ['analytics-reports'],
    queryFn: ({ signal }) => fetchReports(signal),
    retry: false,
  })

  const mayCreate = can(user, 'analytics.report', 'create')
  const mayUpdate = can(user, 'analytics.report', 'update')
  const mayDelete = can(user, 'analytics.report', 'delete')
  const failed = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['report-schedules'] })

  const toggle = useMutation({
    mutationFn: (schedule: ReportSchedule) =>
      updateSchedule(schedule.id, {
        reportId: schedule.reportId,
        cron: schedule.cron,
        // A legacy PDF schedule is refused by the server on any save: switching it off
        // is a save too, and the refusal names the format rather than failing silently.
        format: schedule.format as SaveScheduleRequest['format'],
        recipientEmails: schedule.recipientEmails,
        recipientUserIds: schedule.recipientUserIds,
        isActive: !schedule.isActive,
      }),
    onSuccess: () => {
      setNotice(null)
      refresh()
    },
    onError: failed,
  })
  const remove = useMutation({
    mutationFn: (id: string) => deleteSchedule(id),
    onSuccess: () => {
      setDeleting(null)
      setNotice(null)
      refresh()
    },
    onError: (error: unknown) => {
      setDeleting(null)
      failed(error)
    },
  })

  const all = schedules.data ?? []
  const needle = search.trim().toLowerCase()
  const visible = all
    .filter((s) => filter !== 'off' || !s.isActive)
    .filter((s) => filter !== 'broken' || (s.isActive && !s.nextRunAt))
    .filter((s) => !needle || s.reportName.toLowerCase().includes(needle) || s.recipientEmails.some((e) => e.toLowerCase().includes(needle)))

  const openNew = () => setDraft(emptyDraft())
  const forbidden = schedules.error && isForbidden(schedules.error)

  return (
    <div className="stack" style={{ gap: 14 }}>
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <span>{t('nav.section.analytics')}</span>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{t('nav.reportSchedules')}</span>
      </nav>
      <div className="page-head">
        <h1 className="h1">{t('nav.reportSchedules')}</h1>
        {schedules.isSuccess && all.length > 0 && <span className="muted t-md">{all.length}</span>}
        <span className="spacer" />
        {mayCreate && !forbidden && (
          <button className="btn btn-primary" type="button" onClick={openNew}>
            <Icon name="plus" />
            {t('rm.new')}
          </button>
        )}
      </div>
      {!forbidden && <p className="page-lead">{t('rm.lead')}</p>}

      {notice && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{notice}</span>
        </div>
      )}

      {forbidden ? (
        <StateView
          kind="forbidden"
          boxed
          title={t('rm.forbidden.title')}
          description={t('rm.forbidden.text')}
          actions={<Link className="btn btn-ghost btn-sm" to="/dashboards">{t('rm.toDashboards')}</Link>}
        />
      ) : schedules.error ? (
        <StateView
          kind="error"
          boxed
          title={t('rm.error.title')}
          description={schedules.error instanceof ApiError ? schedules.error.message : t('rm.error.text')}
          actions={
            <button className="btn btn-primary btn-sm" type="button" onClick={() => void schedules.refetch()}>
              <Icon name="refresh" size="sm" />
              {t('state.retry')}
            </button>
          }
          code={schedules.error instanceof ApiError ? String(schedules.error.status) : undefined}
        />
      ) : schedules.isSuccess && all.length === 0 ? (
        <StateView
          kind="empty"
          boxed
          title={t('rm.empty.title')}
          description={t('rm.empty.text')}
          actions={
            mayCreate ? (
              <button className="btn btn-secondary btn-sm" type="button" onClick={openNew}>
                <Icon name="plus" size="sm" />
                {t('rm.new')}
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="toolbar">
            <div className="input-icon wa-search">
              <Icon name="search" />
              <label htmlFor="rm-q" className="sr-only">{t('rm.search')}</label>
              <input id="rm-q" className="input input-sm" placeholder={t('rm.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="vsep" />
            <button className="chip" type="button" aria-pressed={filter === 'off'} onClick={() => setFilter(filter === 'off' ? null : 'off')}>
              {t('rm.chip.off')} <span className="count">{all.filter((s) => !s.isActive).length}</span>
            </button>
            <button className="chip" type="button" aria-pressed={filter === 'broken'} onClick={() => setFilter(filter === 'broken' ? null : 'broken')}>
              {t('rm.chip.broken')} <span className="count">{all.filter((s) => s.isActive && !s.nextRunAt).length}</span>
            </button>
          </div>

          <div className="tbl-wrap" aria-busy={schedules.isLoading}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('report.schedule.report')}</th>
                  <th>{t('rm.col.when')}</th>
                  <th>{t('report.schedule.format')}</th>
                  <th>{t('rm.col.recipients')}</th>
                  <th>{t('report.schedule.nextRun')}</th>
                  <th>{t('report.schedule.lastRun')}</th>
                  <th>{t('rm.col.active')}</th>
                  <th style={{ width: 40 }}><span className="sr-only">{t('rm.col.actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {schedules.isLoading
                  ? Array.from({ length: 6 }, (_, index) => (
                      <tr key={index}>
                        <td><span className="skel" style={{ width: 110 + ((index * 37) % 90) }} /></td>
                        <td><span className="skel" style={{ width: 150 }} /></td>
                        <td><span className="skel" style={{ width: 56, height: 20 }} /></td>
                        <td><span className="skel" style={{ width: 120 }} /></td>
                        <td><span className="skel" style={{ width: 90 }} /></td>
                        <td><span className="skel" style={{ width: 90 }} /></td>
                        <td><span className="skel" style={{ width: 64, height: 18 }} /></td>
                        <td />
                      </tr>
                    ))
                  : visible.map((schedule) => (
                      <tr key={schedule.id} className={schedule.isActive ? undefined : 'is-muted'}>
                        <td>
                          <span className="stack" style={{ gap: 0 }}>
                            <span className="strong">{schedule.reportName}</span>
                            <span className="t-xs muted mono">{schedule.reportCode}</span>
                          </span>
                        </td>
                        <td className="wrap" style={{ maxWidth: 220 }}>{describeCron(schedule.cron, t)}</td>
                        <td>
                          <span className="badge b-outline">
                            <Icon name="file" />
                            {schedule.format.toUpperCase()}
                          </span>
                        </td>
                        <td className="wrap" style={{ maxWidth: 260 }}>
                          {schedule.recipientEmails.length === 0 ? (
                            <span className="muted">—</span>
                          ) : (
                            <span className="stack" style={{ gap: 0 }}>
                              <span className="mono t-sm">{schedule.recipientEmails[0]}</span>
                              {schedule.recipientEmails.length > 1 && (
                                <span className="t-xs muted">{t('rm.more', { count: schedule.recipientEmails.length - 1 })}</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          {/* Активное без следующего срока — сломанное, и это сказано словом. */}
                          {!schedule.isActive ? (
                            <span className="muted">{t('report.schedule.notScheduled')}</span>
                          ) : schedule.nextRunAt ? (
                            <Moment value={schedule.nextRunAt} locale={locale} />
                          ) : (
                            <span className="flag flag-danger">
                              <Icon name="alert" />
                              {t('rm.broken')}
                            </span>
                          )}
                        </td>
                        <td>
                          {schedule.lastRunAt ? (
                            <Moment value={schedule.lastRunAt} locale={locale} />
                          ) : (
                            <span className="muted">{t('rm.neverRan')}</span>
                          )}
                        </td>
                        <td>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={schedule.isActive}
                              disabled={!mayUpdate || toggle.isPending}
                              aria-label={t('rm.activeLabel', { name: schedule.reportName })}
                              onChange={() => toggle.mutate(schedule)}
                            />
                            <span className="track" />
                            <span className="sw-label">{schedule.isActive ? t('rm.on') : t('rm.off')}</span>
                          </label>
                        </td>
                        <td className="num">
                          {(mayUpdate || mayDelete) && (
                            <MoreMenu
                              compact
                              label={t('rm.actions', { name: schedule.reportName })}
                              items={[
                                ...(mayUpdate ? [{ id: 'edit', label: t('rm.edit'), icon: 'settings' as const, onSelect: () => setDraft(draftOf(schedule)) }] : []),
                                ...(mayDelete ? [{ id: 'delete', label: t('rm.deleteAsk'), icon: 'trash' as const, danger: true, onSelect: () => setDeleting(schedule) }] : []),
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
          {schedules.isSuccess && (
            <div className="pager">
              <span>{t('rm.footer', { count: all.length, active: all.filter((s) => s.isActive).length })}</span>
            </div>
          )}
        </>
      )}

      {draft && (
        <ScheduleSheet
          draft={draft}
          reports={reports.data ?? []}
          onCancel={() => setDraft(null)}
          onSaved={() => {
            setDraft(null)
            setNotice(null)
            refresh()
          }}
        />
      )}

      <ConfirmModal
        open={deleting != null}
        title={t('rm.deleteTitle', { name: deleting?.reportName ?? '' })}
        confirmLabel={t('report.delete')}
        pending={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      >
        {t('rm.deleteText')}
      </ConfirmModal>
    </div>
  )
}

function Moment({ value, locale }: { value: string; locale: Locale }) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return <>{value}</>
  return (
    <span className="stack" style={{ gap: 0 }}>
      <span>{new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)}</span>
      <span className="t-xs muted">{new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)}</span>
    </span>
  )
}

// ------------------------------------------------------------------ форма

function ScheduleSheet({
  draft: initial,
  reports,
  onCancel,
  onSaved,
}: {
  draft: Draft
  reports: SavedReport[]
  onCancel: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const [draft, setDraft] = useState<Draft>(initial)
  const [emailText, setEmailText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const patch = (change: Partial<Draft>) => setDraft((current) => ({ ...current, ...change }))

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const cron = buildCron(draft)
  const save = useMutation({
    mutationFn: () => {
      const body: SaveScheduleRequest = {
        reportId: draft.reportId,
        cron,
        format: draft.format,
        recipientEmails: draft.emails,
        isActive: draft.isActive,
      }
      return draft.id ? updateSchedule(draft.id, body) : createSchedule(body)
    },
    onSuccess: onSaved,
    onError: (failure: unknown) => setError(failure instanceof ApiError ? failure.message : t('app.error')),
  })

  const addEmails = () => {
    // Адреса разделяются запятой или пробелом и чистятся от пустых: «a@x.ru, » иначе даёт
    // получателя с пустым адресом, и рассылка падает на нём целиком.
    const parts = emailText.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean)
    if (parts.length === 0) return
    patch({ emails: Array.from(new Set([...draft.emails, ...parts])) })
    setEmailText('')
  }

  const weeklyEmpty = draft.mode === 'weekly' && draft.days.length === 0
  // Без получателей расписание исполняло бы отчёт и выбрасывало результат — сервер это
  // отвергает, форма не даёт и отправить.
  const invalid = !draft.reportId || draft.emails.length === 0 || weeklyEmpty || !cron.trim()

  return (
    <div className="overlay is-sheet wa-overlay" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <aside className="sheet" role="dialog" aria-modal="true" aria-labelledby="rm-sheet-t" style={{ width: 520 }}>
        <div className="sheet-h">
          <div className="stack" style={{ flex: 1, gap: 2 }}>
            <span className="overline">{t('rm.sheet.overline')}</span>
            <span id="rm-sheet-t" className="h2">{draft.id ? t('rm.sheet.edit') : t('rm.new')}</span>
          </div>
          <button className="btn btn-ghost btn-sm btn-icon" type="button" aria-label={t('rm.close')} onClick={onCancel}>
            <Icon name="close" size="sm" />
          </button>
        </div>
        <div className="sheet-b">
          {error && (
            <div className="alert alert-danger" role="alert">
              <Icon name="alert" />
              <span className="fg">{error}</span>
            </div>
          )}
          <div className="field">
            <label className="label" htmlFor="rm-report">
              {t('report.schedule.report')} <span className="req">*</span>
            </label>
            {reports.length === 0 ? (
              <span className="help">{t('rm.noReports')}</span>
            ) : (
              <select id="rm-report" className="input select" value={draft.reportId} onChange={(e) => patch({ reportId: e.target.value })}>
                <option value="">{t('report.schedule.chooseReport')}</option>
                {reports.map((report) => (
                  <option key={report.id} value={report.id}>{report.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="field">
            <span className="label">{t('rm.col.when')}</span>
            <div className="seg" role="group" aria-label={t('rm.col.when')} style={{ width: 'fit-content' }}>
              {(['daily', 'weekly', 'monthly', 'cron'] as const).map((mode) => (
                <button key={mode} type="button" aria-pressed={draft.mode === mode} onClick={() => patch({ mode })}>
                  {t(`rm.mode.${mode}`)}
                </button>
              ))}
            </div>
          </div>

          {draft.mode === 'weekly' && (
            <div className="field">
              <span className="label">{t('rm.days')}</span>
              <div className="wa-days" role="group" aria-label={t('rm.days')}>
                {WEEKDAYS.map((day) => (
                  <button
                    key={day}
                    className="chip"
                    type="button"
                    aria-pressed={draft.days.includes(day)}
                    onClick={() => patch({ days: draft.days.includes(day) ? draft.days.filter((d) => d !== day) : [...draft.days, day] })}
                  >
                    {t(`rm.dow.${day}`)}
                  </button>
                ))}
              </div>
              {weeklyEmpty && <span className="err-text">{t('rm.daysRequired')}</span>}
            </div>
          )}

          {draft.mode === 'cron' ? (
            <div className="field">
              <label className="label" htmlFor="rm-cron">{t('report.schedule.cron')}</label>
              <input id="rm-cron" className="input mono" value={draft.cron} onChange={(e) => patch({ cron: e.target.value })} />
              <span className="help">{t('rm.cronHelp')}</span>
            </div>
          ) : (
            <div className="form-grid">
              {draft.mode === 'monthly' && (
                <div className="field">
                  <label className="label" htmlFor="rm-dom">{t('rm.dayOfMonth')}</label>
                  <input
                    id="rm-dom"
                    className="input"
                    type="number"
                    min={1}
                    max={28}
                    value={draft.dayOfMonth}
                    onChange={(e) => patch({ dayOfMonth: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })}
                  />
                  <span className="help">{t('rm.dayOfMonthHelp')}</span>
                </div>
              )}
              <div className="field">
                <label className="label" htmlFor="rm-time">{t('rm.time')}</label>
                <input id="rm-time" className="input" type="time" value={draft.time} onChange={(e) => patch({ time: e.target.value || '08:00' })} />
                <span className="help">{t('rm.serverTime')}</span>
              </div>
            </div>
          )}

          <div className="alert alert-info" role="status">
            <Icon name="meet" />
            <span className="fg strong">{describeCron(cron, t)}</span>
          </div>

          <div className="field">
            <label className="label" htmlFor="rm-to">
              {t('rm.col.recipients')} <span className="req">*</span> · {draft.emails.length}
            </label>
            <div className="row" style={{ gap: 6 }}>
              <input
                id="rm-to"
                className="input"
                type="email"
                placeholder={t('rm.emailPlaceholder')}
                value={emailText}
                onChange={(e) => setEmailText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addEmails()
                  }
                }}
              />
              <button className="btn btn-secondary btn-sm" type="button" onClick={addEmails} disabled={!emailText.trim()}>
                {t('rm.addEmail')}
              </button>
            </div>
            {draft.emails.length > 0 && (
              <div className="panel" style={{ marginTop: 4 }}>
                {draft.emails.map((email) => (
                  <div key={email} className="list-item" style={{ minHeight: 36, padding: '4px 6px 4px 12px' }}>
                    <Icon name="mail" size="sm" className="muted" />
                    <span className="mono" style={{ flex: 1 }}>{email}</span>
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      type="button"
                      aria-label={t('rm.removeEmail', { email })}
                      onClick={() => patch({ emails: draft.emails.filter((e) => e !== email) })}
                    >
                      <Icon name="close" size="sm" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <span className="help">{t('rm.recipientsHelp')}</span>
          </div>

          <div className="field">
            <span className="label">{t('report.schedule.format')}</span>
            <div className="seg" role="group" aria-label={t('report.schedule.format')} style={{ width: 'fit-content' }}>
              {(['xlsx', 'csv'] as const).map((format) => (
                <button
                  key={format}
                  type="button"
                  aria-pressed={draft.format === format}
                  onClick={() => patch({ format })}
                >
                  {t(`rm.format.${format}`)}
                </button>
              ))}
            </div>
            <span className="help">{t('rm.formatHelp')}</span>
          </div>

          {draft.id && <DeliveryLog scheduleId={draft.id} />}
        </div>
        <div className="sheet-f">
          <span className="hint">{t('rm.escHint')}</span>
          <button className="btn btn-secondary" type="button" onClick={onCancel}>{t('wa.cancel')}</button>
          <button className="btn btn-primary" type="button" disabled={invalid || save.isPending} onClick={() => save.mutate()}>
            {draft.id ? t('rm.save') : t('rm.create')}
          </button>
        </div>
      </aside>
    </div>
  )
}

/**
 * Журнал доставки расписания (#139): последние письма, кому и чем кончилось. Письмо, которое не
 * ушло, показано с причиной словами — «нет права на данные», «почта не подключена»: пустое место
 * читалось бы как «отправлено».
 */
function DeliveryLog({ scheduleId }: { scheduleId: string }) {
  const { t, locale } = useApp()
  const log = useQuery({
    queryKey: ['report-schedule-deliveries', scheduleId],
    queryFn: ({ signal }) => fetchScheduleDeliveries(scheduleId, signal),
    retry: false,
  })
  const when = (value: string) => {
    const date = new Date(value)
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date)
  }
  return (
    <section className="field" aria-label={t('rm.deliveries')}>
      <span className="label">{t('rm.deliveries')}</span>
      {log.error ? (
        <span className="err-text">{log.error instanceof ApiError ? log.error.message : t('app.error')}</span>
      ) : log.isSuccess && log.data.length === 0 ? (
        <span className="help">{t('rm.deliveries.empty')}</span>
      ) : (
        <div className="panel">
          {(log.data ?? []).map((delivery: ReportDelivery) => (
            <div key={delivery.id} className="list-item" style={{ minHeight: 36, padding: '4px 12px', alignItems: 'flex-start' }}>
              <span className="stack" style={{ gap: 0, flex: 1 }}>
                <span>
                  <span className="mono t-sm">{delivery.toAddress ?? '—'}</span>
                  <span className="t-xs muted"> · {when(delivery.dueAt)}</span>
                </span>
                {delivery.lastError != null && delivery.status !== 'sent' && (
                  <span className="t-xs muted">{delivery.lastError}</span>
                )}
                {delivery.status === 'failed' && delivery.retryAfter != null && (
                  <span className="t-xs muted">{t('rm.deliveries.retry', { when: when(delivery.retryAfter) })}</span>
                )}
              </span>
              <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }}>
                <span className={delivery.status === 'sent' ? 'badge b-outline' : delivery.status === 'rejected' || delivery.status === 'failed' ? 'flag flag-danger' : 'muted'}>
                  {t(`rm.delivery.${delivery.status}`)}
                </span>
                {delivery.status === 'sent' && delivery.rowCount != null && (
                  <span className="t-xs muted">{t('rm.deliveries.rows', { count: delivery.rowCount })}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ------------------------------------------------------------------ cron

function emptyDraft(): Draft {
  // XLSX by default: the file is opened in Excel, and there it should add up without the
  // import wizard (#88).
  return { id: null, reportId: '', mode: 'weekly', days: [1], dayOfMonth: 1, time: '08:00', cron: '0 8 * * 1', emails: [], isActive: true, format: 'xlsx' }
}

/** Разбор сохранённого cron в форму; не описываемое формой — режимом «cron» как есть. */
function draftOf(schedule: ReportSchedule): Draft {
  // A legacy PDF schedule opens as XLSX: the form shows the change before it is saved.
  const format = schedule.format === 'csv' ? 'csv' : 'xlsx'
  const base: Draft = { ...emptyDraft(), id: schedule.id, reportId: schedule.reportId, cron: schedule.cron, emails: schedule.recipientEmails, isActive: schedule.isActive, format }
  const parsed = parseCron(schedule.cron)
  return parsed ? { ...base, ...parsed } : { ...base, mode: 'cron' }
}

function parseCron(cron: string): Pick<Draft, 'mode' | 'days' | 'dayOfMonth' | 'time'> | null {
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return null
  const [minute, hour, dom, month, dow] = parts as [string, string, string, string, string]
  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour) || month !== '*') return null
  const time = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
  if (dom === '*' && dow === '*') return { mode: 'daily', days: [1], dayOfMonth: 1, time }
  if (dom === '*' && /^[0-7](,[0-7])*$/.test(dow)) {
    return { mode: 'weekly', days: dow.split(',').map((d) => Number(d) % 7), dayOfMonth: 1, time }
  }
  if (/^\d+$/.test(dom) && dow === '*') return { mode: 'monthly', days: [1], dayOfMonth: Number(dom), time }
  return null
}

function buildCron(draft: Draft): string {
  if (draft.mode === 'cron') return draft.cron.trim()
  const [hour, minute] = draft.time.split(':').map((v) => String(Number(v)))
  if (draft.mode === 'daily') return `${minute} ${hour} * * *`
  if (draft.mode === 'monthly') return `${minute} ${hour} ${draft.dayOfMonth} * *`
  const days = draft.days.slice().sort((a, b) => a - b)
  return `${minute} ${hour} * * ${days.join(',')}`
}

/** «Каждый понедельник и четверг в 8:00» — словами, а не строкой cron. */
export function describeCron(cron: string, t: (key: string, p?: Record<string, string | number>) => string): string {
  const parsed = parseCron(cron)
  if (!parsed) return t('rm.describe.raw', { cron })
  if (parsed.mode === 'daily') return t('rm.describe.daily', { time: parsed.time })
  if (parsed.mode === 'monthly') return t('rm.describe.monthly', { day: parsed.dayOfMonth, time: parsed.time })
  const days = WEEKDAYS.filter((d) => parsed.days.includes(d)).map((d) => t(`rm.dow.${d}`)).join(', ')
  return t('rm.describe.weekly', { days, time: parsed.time })
}
